import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { 
  MessageSquare, 
  Send, 
  Users,
  Search,
  CheckCircle,
  Phone,
  UserCheck,
  Filter,
  FolderPlus,
  Trash2,
  Plus,
  Edit,
  X,
  Lock,
  Globe
} from "lucide-react";
import { buildSmsUrl } from "../lib/sms";

const SMSPage = () => {
  const { token, hasRole, user } = useAuth();
  const ldNaziv = user?.ld_naziv || "LD";
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [message, setMessage] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterGroup, setFilterGroup] = useState("all");
  const [smsGroups, setSmsGroups] = useState([]);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ naziv: "", opis: "", deljena: true });
  const [memberPickerOpen, setMemberPickerOpen] = useState(null); // group id or null
  const [memberPickerSearch, setMemberPickerSearch] = useState("");

  const canSendSMS = hasRole(["predsednik", "gospodar", "tajnik", "blagajnik", "skupinovodja"]);
  const canShareGroup = hasRole(["predsednik", "tajnik", "gospodar", "administrator"]);

  useEffect(() => {
    if (canSendSMS) {
      fetchMembers();
      fetchGroups();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMembers = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/users`, { headers });
      const membersWithPhone = response.data.filter(m => m.telefon && m.telefon.trim() !== "");
      setMembers(membersWithPhone);
    } catch (error) {
      toast.error("Napaka pri nalaganju članov");
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/sms/groups`, { headers });
      setSmsGroups(res.data);
    } catch (e) { /* ignore */ }
  };

  const handleSaveGroup = async (e) => {
    e.preventDefault();
    if (!groupForm.naziv.trim()) {
      toast.error("Vnesite naziv");
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingGroup) {
        await axios.put(`${API}/sms/groups/${editingGroup.id}`, groupForm, { headers });
        toast.success("Skupina posodobljena");
      } else {
        await axios.post(`${API}/sms/groups`, groupForm, { headers });
        toast.success("Skupina ustvarjena");
      }
      setGroupDialogOpen(false);
      setEditingGroup(null);
      setGroupForm({ naziv: "", opis: "", deljena: true });
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri shranjevanju");
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Izbrišem skupino "${group.naziv}"?`)) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/sms/groups/${group.id}`, { headers });
      toast.success("Skupina izbrisana");
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka");
    }
  };

  const handleAddMembersToGroup = async (groupId, ids) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/sms/groups/${groupId}/members`, { clani_ids: ids }, { headers });
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri dodajanju");
    }
  };

  const handleRemoveMemberFromGroup = async (groupId, userId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/sms/groups/${groupId}/members/${userId}`, { headers });
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka");
    }
  };

  const loadGroupIntoSelection = (group) => {
    // Only members with phones get loaded
    const phoneIds = members.filter(m => group.clani_ids?.includes(m.id)).map(m => m.id);
    setSelectedMembers(phoneIds);
    toast.success(`Naloženih ${phoneIds.length} članov iz skupine "${group.naziv}"`);
  };

  const getRoleLabel = (role) => {
    const labels = {
      predsednik: "Predsednik",
      gospodar: "Gospodar",
      blagajnik: "Blagajnik",
      tajnik: "Tajnik",
      upravnik_lovske_koce: "Upravnik koče",
      referent_strelstvo: "Ref. strelstvo",
      skupinovodja: "Skupinovodja",
      clan: "Član"
    };
    return labels[role] || role;
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    // Remove spaces and dashes
    let cleaned = phone.replace(/[\s-]/g, "");
    // Add country code if not present
    if (cleaned.startsWith("0")) {
      cleaned = "+386" + cleaned.substring(1);
    } else if (!cleaned.startsWith("+")) {
      cleaned = "+386" + cleaned;
    }
    return cleaned;
  };

  const filteredMembers = members.filter(m => {
    const fullName = `${m.ime} ${m.priimek}`.toLowerCase();
    const matchesSearch = fullName.includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === "all" || m.vloga === filterRole;
    const matchesGroup = filterGroup === "all" || (m.skupina && String(m.skupina) === filterGroup);
    return matchesSearch && matchesRole && matchesGroup;
  });

  // Find skupinovodja per group for label display
  const groupLeaders = (() => {
    const leaders = {};
    members.forEach(m => {
      if (m.vloga === "skupinovodja" && m.skupina) {
        leaders[String(m.skupina)] = m.priimek;
      }
    });
    return leaders;
  })();

  const toggleMember = (memberId) => {
    setSelectedMembers(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const selectAll = () => {
    const allIds = filteredMembers.map(m => m.id);
    setSelectedMembers(allIds);
  };

  const deselectAll = () => {
    setSelectedMembers([]);
  };

  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const handleSendSMS = () => {
    if (selectedMembers.length === 0) {
      toast.error("Izberite vsaj enega prejemnika");
      return;
    }
    if (!message.trim()) {
      toast.error("Vnesite sporočilo");
      return;
    }

    // Get selected members' phone numbers
    const selectedPhones = members
      .filter(m => selectedMembers.includes(m.id))
      .map(m => formatPhoneNumber(m.telefon))
      .filter(p => p);

    if (selectedPhones.length === 0) {
      toast.error("Izbrani člani nimajo telefonskih številk");
      return;
    }

    // Encode message for URL
    const url = buildSmsUrl(selectedPhones, message);
    window.location.href = url;

    toast.success(`SMS aplikacija odprta za ${selectedPhones.length} prejemnikov`);
  };

  const handleSendIndividually = () => {
    if (selectedMembers.length === 0) {
      toast.error("Izberite vsaj enega prejemnika");
      return;
    }
    if (!message.trim()) {
      toast.error("Vnesite sporočilo");
      return;
    }

    const selectedPhones = members
      .filter(m => selectedMembers.includes(m.id))
      .map(m => ({ name: `${m.ime} ${m.priimek}`, phone: formatPhoneNumber(m.telefon) }))
      .filter(p => p.phone);

    // Store in sessionStorage for sequential sending
    sessionStorage.setItem('smsQueue', JSON.stringify(selectedPhones));
    sessionStorage.setItem('smsMessage', message);
    sessionStorage.setItem('smsIndex', '0');

    // Open first SMS
    if (selectedPhones.length > 0) {
      window.location.href = buildSmsUrl([selectedPhones[0].phone], message);
      toast.info(`Pošiljanje 1/${selectedPhones.length}: ${selectedPhones[0].name}`);
    }
  };

  if (!canSendSMS) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">Nimate dovoljenja za pošiljanje SMS</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const roles = [
    { value: "all", label: "Vsi" },
    { value: "predsednik", label: "Predsednik" },
    { value: "gospodar", label: "Gospodar" },
    { value: "tajnik", label: "Tajnik" },
    { value: "blagajnik", label: "Blagajnik" },
    { value: "skupinovodja", label: "Skupinovodja" },
    { value: "clan", label: "Člani" }
  ];

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="sms-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-primary flex items-center gap-2">
          <MessageSquare className="h-6 w-6 sm:h-8 sm:w-8" />
          Pošlji SMS
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          SMS se pošlje preko vaše mobilne naprave
        </p>
      </div>

      <Tabs defaultValue="posiljanje" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="posiljanje" data-testid="tab-posiljanje">
            <Send className="h-4 w-4 mr-1" />
            Pošiljanje
          </TabsTrigger>
          <TabsTrigger value="skupine" data-testid="tab-skupine">
            <Users className="h-4 w-4 mr-1" />
            Skupine
          </TabsTrigger>
        </TabsList>

        <TabsContent value="posiljanje" className="mt-4">
          {smsGroups.length > 0 && (
            <div className="mb-3 p-3 rounded-lg bg-muted/40 border">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground font-medium">Hitra izbira skupine:</span>
                {smsGroups.map(g => (
                  <Button
                    key={g.id}
                    size="sm"
                    variant="outline"
                    onClick={() => loadGroupIntoSelection(g)}
                    className="text-xs h-7"
                    data-testid={`load-group-${g.id}`}
                  >
                    {g.deljena ? <Globe className="h-3 w-3 mr-1" /> : <Lock className="h-3 w-3 mr-1" />}
                    {g.naziv} ({g.clani_ids?.length || 0})
                  </Button>
                ))}
              </div>
            </div>
          )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recipients Selection */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4" />
                Prejemniki ({selectedMembers.length})
              </CardTitle>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={selectAll}>
                  Izberi vse
                </Button>
                <Button size="sm" variant="ghost" onClick={deselectAll}>
                  Počisti
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Išči člana..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex gap-2">
                <select
                  value={filterGroup}
                  onChange={(e) => setFilterGroup(e.target.value)}
                  className="px-3 py-2 border rounded-md text-sm bg-background flex-1 sm:flex-initial"
                  data-testid="sms-filter-group"
                >
                  <option value="all">Vse skupine</option>
                  <option value="1">1. skupina{groupLeaders["1"] ? ` - ${groupLeaders["1"]}` : ""}</option>
                  <option value="2">2. skupina{groupLeaders["2"] ? ` - ${groupLeaders["2"]}` : ""}</option>
                  <option value="3">3. skupina{groupLeaders["3"] ? ` - ${groupLeaders["3"]}` : ""}</option>
                </select>
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className="px-3 py-2 border rounded-md text-sm bg-background flex-1 sm:flex-initial"
                >
                  {roles.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Members List */}
            <ScrollArea className="h-[350px] border rounded-lg">
              <div className="p-2 space-y-1">
                {filteredMembers.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Ni članov s telefonsko številko
                  </p>
                ) : (
                  filteredMembers.map((member) => (
                    <div
                      key={member.id}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                        selectedMembers.includes(member.id) 
                          ? 'bg-primary/10 border border-primary/30' 
                          : 'hover:bg-muted'
                      }`}
                      onClick={() => toggleMember(member.id)}
                    >
                      <Checkbox
                        checked={selectedMembers.includes(member.id)}
                        onCheckedChange={() => toggleMember(member.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {member.ime} {member.priimek}
                        </p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {member.telefon}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {getRoleLabel(member.vloga)}
                      </Badge>
                      {member.skupina && (
                        <Badge variant="secondary" className="text-xs ml-1">
                          {member.skupina}. sk.
                        </Badge>
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>

            <p className="text-xs text-muted-foreground">
              Prikazanih {filteredMembers.length} članov s telefonsko številko
            </p>
          </CardContent>
        </Card>

        {/* Message Composition */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Send className="h-4 w-4" />
              Sporočilo
            </CardTitle>
            <CardDescription>
              Napišite SMS sporočilo (max 160 znakov za 1 SMS)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Vnesite sporočilo..."
                rows={6}
                className="resize-none"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{message.length} znakov</span>
                <span>{Math.ceil(message.length / 160) || 1} SMS</span>
              </div>
            </div>

            {/* Quick Templates */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Hitre predloge:</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setMessage(`Vabljeni na lovski dogodek. Več informacij na spletni strani ${ldNaziv}.`)}
                >
                  Dogodek
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setMessage("Opomnik: Delovna akcija je v soboto. Prosimo za udeležbo.")}
                >
                  Delovna akcija
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setMessage(`Sestanek ${ldNaziv}. Obvezna prisotnost. Podrobnosti na spletni strani.`)}
                >
                  Sestanek
                </Button>
              </div>
            </div>

            {/* Selected Recipients Summary */}
            {selectedMembers.length > 0 && (
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-sm font-medium flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-green-600" />
                  {selectedMembers.length} prejemnikov izbranih
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {members.filter(m => selectedMembers.includes(m.id)).map(m => `${m.ime} ${m.priimek}`).slice(0, 3).join(", ")}
                  {selectedMembers.length > 3 && ` in še ${selectedMembers.length - 3}`}
                </p>
              </div>
            )}

            {/* Send Buttons */}
            <div className="space-y-2">
              <Button 
                className="w-full" 
                size="lg"
                onClick={handleSendSMS}
                disabled={selectedMembers.length === 0 || !message.trim()}
              >
                <Send className="h-4 w-4 mr-2" />
                Pošlji SMS ({selectedMembers.length})
              </Button>
              
              {selectedMembers.length > 1 && (
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={handleSendIndividually}
                  disabled={!message.trim()}
                >
                  Pošlji posamično (1 za 1)
                </Button>
              )}
            </div>

            <p className="text-xs text-muted-foreground text-center">
              💡 SMS se odpre v vaši SMS aplikaciji. Stroški po vašem paketu.
            </p>
          </CardContent>
        </Card>
      </div>
        </TabsContent>

        <TabsContent value="skupine" className="mt-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-semibold">Moje SMS skupine</h2>
              <p className="text-xs text-muted-foreground">
                Ustvarite skupine članov za hitro izbiro pri pošiljanju
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => {
                setEditingGroup(null);
                setGroupForm({ naziv: "", opis: "", deljena: canShareGroup });
                setGroupDialogOpen(true);
              }}
              data-testid="new-sms-group"
            >
              <FolderPlus className="h-4 w-4 mr-1" />
              Nova skupina
            </Button>
          </div>

          {smsGroups.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Še ni skupin. Ustvarite prvo!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {smsGroups.map(group => {
                const groupMembers = members.filter(m => group.clani_ids?.includes(m.id));
                const canEdit = group.ustvaril_id === undefined || true; // backend enforces
                return (
                  <Card key={group.id} data-testid={`sms-group-card-${group.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <CardTitle className="text-base flex items-center gap-2 break-words">
                            {group.deljena ? (
                              <Globe className="h-4 w-4 text-blue-600 shrink-0" />
                            ) : (
                              <Lock className="h-4 w-4 text-amber-600 shrink-0" />
                            )}
                            <span className="break-words">{group.naziv}</span>
                          </CardTitle>
                          {group.opis && (
                            <p className="text-xs text-muted-foreground mt-1 break-words">{group.opis}</p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            Ustvaril: {group.ustvaril_ime}
                          </p>
                        </div>
                        <Badge variant="outline" className="shrink-0">
                          {group.clani_ids?.length || 0}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2 pb-3">
                      {groupMembers.length > 0 && (
                        <ScrollArea className="max-h-32">
                          <div className="flex flex-wrap gap-1">
                            {groupMembers.map(m => (
                              <Badge
                                key={m.id}
                                variant="secondary"
                                className="text-xs gap-1"
                              >
                                {m.ime} {m.priimek}
                                <button
                                  onClick={() => handleRemoveMemberFromGroup(group.id, m.id)}
                                  className="hover:text-red-600"
                                  data-testid={`remove-from-group-${group.id}-${m.id}`}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        </ScrollArea>
                      )}
                      <div className="flex flex-wrap gap-2 pt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setMemberPickerOpen(group.id);
                            setMemberPickerSearch("");
                          }}
                          data-testid={`add-members-${group.id}`}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Dodaj člane
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => loadGroupIntoSelection(group)}
                        >
                          <Users className="h-3 w-3 mr-1" />
                          Naloži v SMS
                        </Button>
                        {canEdit && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingGroup(group);
                                setGroupForm({
                                  naziv: group.naziv,
                                  opis: group.opis || "",
                                  deljena: group.deljena
                                });
                                setGroupDialogOpen(true);
                              }}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteGroup(group)}
                              className="text-red-600 hover:bg-red-50"
                              data-testid={`delete-group-${group.id}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Group Dialog */}
      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent className="max-w-md" data-testid="group-dialog">
          <DialogHeader>
            <DialogTitle>{editingGroup ? "Uredi skupino" : "Nova skupina"}</DialogTitle>
            <DialogDescription>
              Skupina za hitro izbiro članov pri pošiljanju SMS sporočil
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveGroup} className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Naziv *</Label>
              <Input
                value={groupForm.naziv}
                onChange={(e) => setGroupForm({ ...groupForm, naziv: e.target.value })}
                placeholder="npr. Komisija za odstrel"
                required
                data-testid="group-naziv-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Opis (opcijsko)</Label>
              <Textarea
                value={groupForm.opis}
                onChange={(e) => setGroupForm({ ...groupForm, opis: e.target.value })}
                rows={2}
              />
            </div>
            {canShareGroup && (
              <div className="flex items-center gap-2 p-2 rounded border bg-muted/30">
                <Checkbox
                  id="deljena"
                  checked={groupForm.deljena}
                  onCheckedChange={(v) => setGroupForm({ ...groupForm, deljena: !!v })}
                />
                <Label htmlFor="deljena" className="text-sm cursor-pointer flex items-center gap-1">
                  {groupForm.deljena ? <Globe className="h-4 w-4 text-blue-600" /> : <Lock className="h-4 w-4 text-amber-600" />}
                  {groupForm.deljena ? "Deljena (vidijo vsi)" : "Zasebna (vidim samo jaz)"}
                </Label>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setGroupDialogOpen(false)}>
                Prekliči
              </Button>
              <Button type="submit" data-testid="save-group">
                {editingGroup ? "Posodobi" : "Ustvari"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Member Picker Dialog */}
      <Dialog open={!!memberPickerOpen} onOpenChange={(o) => { if (!o) setMemberPickerOpen(null); }}>
        <DialogContent className="max-w-md" data-testid="member-picker-dialog">
          <DialogHeader>
            <DialogTitle>Dodaj člane v skupino</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              placeholder="Išči člana..."
              value={memberPickerSearch}
              onChange={(e) => setMemberPickerSearch(e.target.value)}
            />
            <ScrollArea className="h-72 border rounded">
              <div className="p-2 space-y-1">
                {(() => {
                  const grp = smsGroups.find(g => g.id === memberPickerOpen);
                  const inGroup = new Set(grp?.clani_ids || []);
                  const filtered = members.filter(m => 
                    !inGroup.has(m.id) &&
                    `${m.ime} ${m.priimek}`.toLowerCase().includes(memberPickerSearch.toLowerCase())
                  );
                  if (filtered.length === 0) {
                    return <p className="text-xs text-muted-foreground text-center py-4">Ni rezultatov</p>;
                  }
                  return filtered.map(m => (
                    <button
                      key={m.id}
                      onClick={() => {
                        handleAddMembersToGroup(memberPickerOpen, [m.id]);
                      }}
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-muted flex items-center justify-between gap-2"
                      data-testid={`picker-add-${m.id}`}
                    >
                      <span className="text-sm break-words">{m.ime} {m.priimek}</span>
                      <Plus className="h-3 w-3 shrink-0" />
                    </button>
                  ));
                })()}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button onClick={() => setMemberPickerOpen(null)}>Končano</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SMSPage;
